import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/transcriptionProviders/")({
  component: TranscriptionProvidersIndexPage,
});

function TranscriptionProvidersIndexPage() {
  return <TranscriptionProvidersIndexPageComponent />;
}

// Exported for testing
export function TranscriptionProvidersIndexPageComponent() {
  const [transcriptionProviders, setTranscriptionProviders] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("transcriptionProviders")
      .then((options) => {
        if (!active) return;
        setTranscriptionProviders(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching transcription providers:", err);
        setError("Failed to load transcription providers.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Transcription Providers"
      description="The speech-to-text providers agents can use during calls - each option here populates a voice agent's transcriptionProvider field."
      items={transcriptionProviders}
      loading={loading}
      error={error}
      getItemKey={(transcriptionProvider) => transcriptionProvider.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(transcriptionProvider) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{transcriptionProvider.label}</CardTitle>
                <CardDescription>
                  {transcriptionProvider.description || `Value: ${transcriptionProvider.value}`}
                </CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Order: {transcriptionProvider.order ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/dashboard/settings/transcriptionProviders/$id"
                  params={{ id: transcriptionProvider.id }}
                >
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/transcriptionProviders/new"
      createLabel="Create Transcription Provider"
      emptyMessage="No transcription providers yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
